import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { NotificationsService } from '@mt-ng2/notifications-module';
import { markAllFormFieldsAsTouched } from '@mt-ng2/common-functions';

import { OfficeService } from '../services/office.service';
import { IOffice } from '../../model/interfaces/office';
import { OfficeDynamicConfig } from '../office.dynamic-config';
import { finalize } from 'rxjs/operators';
import { LocationService } from '../../locations/services/location.service';
import { ILocation } from '../../model/interfaces/location';
import { DynamicField, DynamicLabel, IDynamicFormConfig } from '@mt-ng2/dynamic-form';

@Component({
    selector: 'app-office-basic-info',
    templateUrl: './office-basic-info.component.html',
    standalone: false
})
export class OfficeBasicInfoComponent implements OnInit {
    @Input() office: IOffice;
    @Input() canEdit: boolean;

    isEditing: boolean;
    isHovered: boolean;
    viewOnly: DynamicLabel[] = [];
    formObject: DynamicField[] = [];
    formFactory: OfficeDynamicConfig<IOffice>;
    doubleClickIsDisabled = false;
    locations: ILocation[];
    weekdaySchedules: { day: string; enabled: boolean; startTime: string; endTime: string }[] = [];

    constructor(
        private officeService: OfficeService,
        private notificationsService: NotificationsService,
        private router: Router,
        private locationService: LocationService,
    ) {}

    ngOnInit(): void {
        this.isEditing = false;
        this.locationService.getAll().subscribe((locations) => {
            this.locations = locations;
            this.setConfig();
        });
    }

    setConfig(): void {
        let config: IDynamicFormConfig;
        this.formFactory = new OfficeDynamicConfig<IOffice>(this.office, this.locations);

        if (this.office.Id === 0) {
            // new office
            this.isEditing = true;
            config = this.formFactory.getForCreate();
        } else {
            // existing office
            config = this.formFactory.getForUpdate();
        }

        this.viewOnly = config?.viewOnly?.map((x) => new DynamicLabel(x));
        this.formObject = config.formObject?.map((x) => new DynamicField(x));
        this.setWeekdaySchedules();
    }

    private setWeekdaySchedules(): void {
        this.weekdaySchedules = [
            {
                day: 'Every Monday',
                enabled: !!this.office?.DefaultWeekdayMon,
                startTime: this.formatScheduleTime(this.office?.CheckInStartTimeMon),
                endTime: this.formatScheduleTime(this.office?.CheckInEndTimeMon),
            },
            {
                day: 'Every Tuesday',
                enabled: !!this.office?.DefaultWeekdayTue,
                startTime: this.formatScheduleTime(this.office?.CheckInStartTimeTue),
                endTime: this.formatScheduleTime(this.office?.CheckInEndTimeTue),
            },
            {
                day: 'Every Wednesday',
                enabled: !!this.office?.DefaultWeekdayWed,
                startTime: this.formatScheduleTime(this.office?.CheckInStartTimeWed),
                endTime: this.formatScheduleTime(this.office?.CheckInEndTimeWed),
            },
            {
                day: 'Every Thursday',
                enabled: !!this.office?.DefaultWeekdayThu,
                startTime: this.formatScheduleTime(this.office?.CheckInStartTimeThu),
                endTime: this.formatScheduleTime(this.office?.CheckInEndTimeThu),
            },
            {
                day: 'Every Friday',
                enabled: !!this.office?.DefaultWeekdayFri,
                startTime: this.formatScheduleTime(this.office?.CheckInStartTimeFri),
                endTime: this.formatScheduleTime(this.office?.CheckInEndTimeFri),
            },
            {
                day: 'Every Saturday',
                enabled: !!this.office?.DefaultWeekdaySat,
                startTime: this.formatScheduleTime(this.office?.CheckInStartTimeSat),
                endTime: this.formatScheduleTime(this.office?.CheckInEndTimeSat),
            },
            {
                day: 'Every Sunday',
                enabled: !!this.office?.DefaultWeekdaySun,
                startTime: this.formatScheduleTime(this.office?.CheckInStartTimeSun),
                endTime: this.formatScheduleTime(this.office?.CheckInEndTimeSun),
            },
        ];
    }

    private formatScheduleTime(value?: string): string {
        if (!value) {
            return '--:-- --';
        }

        const trimmed = value.trim();
        if (!trimmed) {
            return '--:-- --';
        }

        // Handle values in 24-hour time format from native time inputs.
        const twentyFourHourMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
        if (twentyFourHourMatch) {
            const hours = Number(twentyFourHourMatch[1]);
            const minutes = twentyFourHourMatch[2];

            if (!Number.isNaN(hours) && hours >= 0 && hours <= 23) {
                const period = hours >= 12 ? 'PM' : 'AM';
                const twelveHour = hours % 12 || 12;
                return `${String(twelveHour).padStart(2, '0')}:${minutes} ${period}`;
            }
        }

        return trimmed;
    }

    edit(): void {
        if (this.canEdit) {
            this.isEditing = true;
        }
    }

    cancelClick(): void {
        if (this.office.Id === 0) {
            void this.router.navigate(['/offices']);
        } else {
            this.isEditing = false;
        }
    }

    formSubmitted(form: UntypedFormGroup): void {
        if (form.valid) {
            if (!this.validateCheckInTimePairs()) {
                return;
            }

            const formValues = form.value.Office as IOffice;
            formValues.NonTwicFee = formValues.NonTwicFee ?? 0;
            formValues.TwicFee = formValues.TwicFee ?? 0;

            this.formFactory.assignFormValues(this.office, formValues);
            this.office.DaeOffice = formValues.DaeOffice;

            if (!this.office.Id || this.office.Id === 0) {
                // handle new office save
                this.officeService
                    .create(this.office)
                    .pipe(finalize(() => (this.doubleClickIsDisabled = false)))
                    .subscribe((answer) => {
                        void this.router.navigate([`/offices/${answer}`]);
                        this.success();
                        this.officeService.emitChange(this.office);
                    });
            } else {
                // handle existing office save
                this.officeService
                    .updateVersion(this.office)
                    .pipe(finalize(() => (this.doubleClickIsDisabled = false)))
                    .subscribe((success) => {
                        this.office.Version = success;
                        this.isEditing = false;
                        this.success();
                        this.officeService.emitChange(this.office);
                        this.setConfig();
                    });
            }
        } else {
            markAllFormFieldsAsTouched(form);
            this.error();
        }
    }

    private validateCheckInTimePairs(): boolean {
        const weekdayTimes = [
            { day: 'Monday', start: this.office?.CheckInStartTimeMon, end: this.office?.CheckInEndTimeMon },
            { day: 'Tuesday', start: this.office?.CheckInStartTimeTue, end: this.office?.CheckInEndTimeTue },
            { day: 'Wednesday', start: this.office?.CheckInStartTimeWed, end: this.office?.CheckInEndTimeWed },
            { day: 'Thursday', start: this.office?.CheckInStartTimeThu, end: this.office?.CheckInEndTimeThu },
            { day: 'Friday', start: this.office?.CheckInStartTimeFri, end: this.office?.CheckInEndTimeFri },
            { day: 'Saturday', start: this.office?.CheckInStartTimeSat, end: this.office?.CheckInEndTimeSat },
            { day: 'Sunday', start: this.office?.CheckInStartTimeSun, end: this.office?.CheckInEndTimeSun },
        ];

        const invalidDays = weekdayTimes.filter((x) => this.hasTimeValue(x.start) !== this.hasTimeValue(x.end)).map((x) => x.day);

        if (invalidDays.length > 0) {
            this.notificationsService.error(`Please provide both check-in start and end times for: ${invalidDays.join(', ')}.`);
            return false;
        }

        return true;
    }

    private hasTimeValue(value?: string): boolean {
        return !!value && value.trim().length > 0;
    }

    error(): void {
        this.notificationsService.error('Save failed.  Please check the form and try again.');
    }

    success(): void {
        this.notificationsService.success('Office saved successfully.');
    }
}
